import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KTSVG, SimpleAlert } from '../../../../../rbts/helpers';
import { useObtenerRecintoPorID } from '../hooks/RecintosHook';
import { crearRecintoService } from '../../../../service/recintos/Recintos';

type Props = {
  show: boolean;
  handleClose: (refresh: boolean) => void;
  id: number;
};

const validationSchema = Yup.object().shape({
  nombre: Yup.string()
    .trim()
    .required('El nombre del recinto es requerido.')
    .max(100, 'Máximo 100 caracteres.'),
  direccion: Yup.string().nullable(),
  aforo_max: Yup.number()
    .typeError('Debe ser un número.')
    .required('El aforo máximo es requerido.')
    .positive('Debe ser mayor a cero.')
    .integer('Debe ser un número entero.'),
});

const ModalCRU: React.FC<Props> = ({ show, handleClose, id }) => {
  const [loading, setLoading] = useState(false);
  const { initValues } = useObtenerRecintoPorID(id);

  const form = useFormik({
    initialValues: initValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await crearRecintoService(values);
        SimpleAlert(id > 0 ? 'Recinto actualizado correctamente' : 'Recinto creado correctamente', 2500, 'success');
        form.resetForm();
        handleClose(true);
      } catch (error: any) {
        const msg = error?.response?.data?.message || 'Error al guardar el recinto.';
        SimpleAlert(msg, 3000, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal
      id="kt_modal_recinto"
      tabIndex={-1}
      aria-hidden="true"
      backdrop="static"
      dialogClassName="modal-dialog mw-600px"
      show={show}
      onHide={() => {
        form.resetForm();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">{id > 0 ? 'Editar recinto' : 'Nuevo recinto'}</h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              form.resetForm();
              handleClose(false);
            }}
          >
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body mx-5">
          <form className="form" onSubmit={form.handleSubmit}>
            <div className="row mb-3">
              {/* Nombre */}
              <div className="col-12 mb-5">
                <label className="required fw-bold fs-6 mb-2">Nombre</label>
                <input
                  className="form-control"
                  placeholder="Ej: Cintermex"
                  maxLength={100}
                  {...form.getFieldProps('nombre')}
                />
                {form.touched.nombre && form.errors.nombre && (
                  <div className="fv-plugins-message-container text-danger">
                    <span>{form.errors.nombre}</span>
                  </div>
                )}
              </div>

              {/* Dirección */}
              <div className="col-12 mb-5">
                <label className="fw-bold fs-6 mb-2">Dirección</label>
                <input
                  className="form-control"
                  placeholder="Ej: Av. Fundidora 501, Monterrey"
                  maxLength={255}
                  {...form.getFieldProps('direccion')}
                />
              </div>

              {/* Aforo máximo */}
              <div className="col-12 mb-5">
                <label className="required fw-bold fs-6 mb-2">Aforo máximo (personas)</label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  placeholder="Ej: 8000"
                  {...form.getFieldProps('aforo_max')}
                />
                {form.touched.aforo_max && form.errors.aforo_max && (
                  <div className="fv-plugins-message-container text-danger">
                    <span>{form.errors.aforo_max as string}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-6">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  form.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Espere...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                ) : (
                  <span className="indicator-label">Guardar</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { ModalCRU };
